import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CreditorService } from '../creditor.service';
import { AuthService } from '../../auth/auth.service';
import { CreditorGroupInfo } from '../../interfaces/creditorGroupInfo.interface';
import { CreditorGroupInvitationDetails } from '../../interfaces/creditorGroupInvitationDetails.interface';
import { YesNoModalService } from '../../modal/yes-no.modal.service';

export const PENDING_GROUP_TOKEN_KEY = 'pendingGroupInviteToken';

@Component({
    selector: 'app-creditor-group-accept',
    templateUrl: './creditor-group-accept.component.html',
    styleUrls: ['./creditor-group-accept.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorGroupAcceptComponent implements OnInit {
  public token: string;
  public details: CreditorGroupInvitationDetails | null = null;
  public isLoading = true;
  public isAccepting = false;
  public isLeavingGroup = false;
  public accepted = false;
  public error: string | null = null;
  public isAlreadyInAGroup = false;

  private currentGroup: CreditorGroupInfo | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private creditorService: CreditorService,
    private authService: AuthService,
    private yesNoModal: YesNoModalService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.params['token'];

    this.authService.isLoggedIn().subscribe(loggedIn => {
      if (!loggedIn) {
        sessionStorage.setItem(PENDING_GROUP_TOKEN_KEY, this.token);
        this.router.navigate(['/borgenar']);
        return;
      }
      this.loadDetails();
    });
  }

  private loadDetails(): void {
    this.isLoading = true;
    forkJoin({
      details: this.creditorService.getGroupInvitationDetails(this.token),
      group: this.creditorService.getMyGroup().pipe(catchError(() => of(null)))
    }).subscribe(
      ({ details, group }) => {
        this.details = details;
        this.currentGroup = group;
        const emptyGuid = '00000000-0000-0000-0000-000000000000';
        this.isAlreadyInAGroup = !!group?.groupId && group.groupId !== emptyGuid;
        this.isLoading = false;
      },
      () => {
        this.error = this.translate.instant('group.error-not-found');
        this.isLoading = false;
      }
    );
  }

  public leaveGroup(): void {
    const currentMember = this.currentGroup?.members?.find(member => member.isCurrentUser);
    if (!currentMember) return;

    this.yesNoModal.open(
      this.translate.instant('group.modal-leave-accept-title'),
      this.translate.instant('group.modal-leave-accept-message'),
      {
        buttonCallback: () => {
          this.isLeavingGroup = true;
          this.creditorService.removeGroupMember(currentMember.userId).subscribe(
            () => {
              this.isLeavingGroup = false;
              this.loadDetails();
            },
            () => {
              this.isLeavingGroup = false;
              this.error = this.translate.instant('group.error-leave-failed');
            }
          );
        }
      }
    );
  }

  public accept(): void {
    this.isAccepting = true;
    this.creditorService.acceptGroupInvitation(this.token).subscribe(
      () => {
        this.isAccepting = false;
        this.accepted    = true;
        sessionStorage.removeItem(PENDING_GROUP_TOKEN_KEY);
        setTimeout(() => this.router.navigate(['/borgenar/start']), 2000);
      },
      err => {
        this.isAccepting = false;
        console.error(err);
        if (err?.status === 400) {
          this.loadDetails();
        } else {
          this.error = this.translate.instant('group.error-accept-failed');
        }
      }
    );
  }

  public decline(): void {
    sessionStorage.removeItem(PENDING_GROUP_TOKEN_KEY);
    this.router.navigate(['/borgenar/start']);
  }
}
